import React, { useState } from 'react';
import { fileUrl } from '@/lib/api';
import { Trash2, CornerDownRight, Heart } from 'lucide-react';
import { useI18n } from '@/i18n';
import AddFriendButton from '@/components/AddFriendButton';
import ChatComposer from '@/components/ChatComposer';
import UserLink from '@/components/UserLink';

function ReplyForm({ commentId, authorName, onSubmit, onCancel }) {
  const { t } = useI18n();

  const submit = async ({ text, image_path }) => {
    try {
      await onSubmit(commentId, { text, image_path });
      onCancel();
    } catch {}
  };

  return (
    <div className="mt-2 ml-2">
      <ChatComposer
        onSubmit={submit}
        placeholder={`${t('spot.comment.reply_to')} ${authorName}...`}
        rows={2}
        sendVariant="cyan"
        testIdPrefix={`comment-reply-${commentId}`}
        autoFocus
      />
    </div>
  );
}

function CommentRow({ c, isReply, canModerate, onReplyClick, onDelete, onLike, currentUserId, onImageClick }) {
  const { t } = useI18n();
  const liked = currentUserId && (c.likes || []).includes(currentUserId);
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`spot-comment-${c.id}`}>
      <div className="flex items-center gap-2 text-xs text-white/50">
        <div className="w-5 h-5 rounded-full bg-[#00F0FF] text-black grid place-items-center text-[10px] font-bold">
          {c.author_name && c.author_name[0] ? c.author_name[0].toUpperCase() : '?'}
        </div>
        <UserLink userId={c.author_id} name={c.author_name} className="font-semibold text-white/80" />
        <AddFriendButton userId={c.author_id} userName={c.author_name} variant="icon" size="xs" />
        <span>
          · {new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
        </span>
        {canModerate(c) && (
          <button
            type="button"
            data-testid={`comment-delete-${c.id}`}
            onClick={() => onDelete(c)}
            className="ml-auto text-red-300/70 hover:text-red-400 text-xs"
            title={t('common.delete')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {c.text && <p className="text-sm text-white/80 mt-2 whitespace-pre-wrap">{c.text}</p>}
      {c.image_path && (
        <a href={fileUrl(c.image_path)} target="_blank" rel="noreferrer" className="block mt-2" data-testid={`comment-image-${c.id}`}>
          <img src={fileUrl(c.image_path)} alt="" className="max-h-64 rounded-lg border border-white/10 object-cover" />
        </a>
      )}
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          data-testid={`comment-like-${c.id}`}
          onClick={() => onLike(c)}
          className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider transition ${
            liked ? 'text-[#FF007F]' : 'text-white/40 hover:text-[#FF007F]'
          }`}
        >
          <Heart className={`w-3 h-3 ${liked ? 'fill-current' : ''}`} />
          {(c.likes_count || 0) > 0 ? c.likes_count : t('spot.comment.like')}
        </button>
        {!isReply && (
          <button
            type="button"
            data-testid={`comment-reply-${c.id}`}
            onClick={() => onReplyClick(c.id)}
            className="text-[11px] uppercase tracking-wider text-white/40 hover:text-[#00F0FF] inline-flex items-center gap-1"
          >
            <CornerDownRight className="w-3 h-3" />
            {t('spot.comment.reply')}
          </button>
        )}
      </div>
    </div>
  );
}

export default function CommentThread({ tree, onReply, onDelete, onLike, canModerate, currentUserId, onImageClick }) {
  const [openReplyFor, setOpenReplyFor] = useState(null);

  const handleReplyClick = (id) => {
    setOpenReplyFor((cur) => (cur === id ? null : id));
  };

  return (
    <div className="space-y-3">
      {tree.map((c) => (
        <div key={c.id}>
          <CommentRow
            c={c}
            isReply={false}
            canModerate={canModerate}
            onReplyClick={handleReplyClick}
            onDelete={onDelete}
            onLike={onLike}
            currentUserId={currentUserId}
            onImageClick={onImageClick}
          />

          {openReplyFor === c.id && (
            <ReplyForm
              commentId={c.id}
              authorName={c.author_name}
              onSubmit={onReply}
              onCancel={() => setOpenReplyFor(null)}
            />
          )}

          {c.replies && c.replies.length > 0 && (
            <div className="ml-5 pl-4 border-l border-white/10 mt-2 space-y-2">
              {c.replies.map((r) => (
                <CommentRow
                  key={r.id}
                  c={r}
                  isReply={true}
                  canModerate={canModerate}
                  onReplyClick={() => {}}
                  onDelete={onDelete}
                  onLike={onLike}
                  currentUserId={currentUserId}
                  onImageClick={onImageClick}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
